import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator, PriorityBadge } from '@/modules/wms/components/common/StatusIndicator';
import { BarcodeScanner } from '@/shared/components/scanner/BarcodeScanner';
import { ScanWorkflowDialog } from '@/modules/wms/components/scanner/ScanWorkflowDialog';
import { LabelPreviewModal } from '@/modules/wms/components/LabelPreviewModal';
import { packingWorkflowSteps, samplePackingItems } from '@/modules/wms/lib/scanWorkflowConfig';
import { packingAPI } from '@/shared/services/api';
import {
  Search,
  Package,
  Box,
  CheckCircle2,
  Clock,
  ArrowRight,
  ScanLine,
  Printer,
  Tag,
  ChevronRight,
  Loader2,
  Eye } from
'lucide-react';
import { toast } from 'sonner';

export const Packing = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [labelData, setLabelData] = useState(null);
  const [selectedCartonId, setSelectedCartonId] = useState(null);
  const [orderStatuses, setOrderStatuses] = useState({});
  const [packingQueue, setPackingQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ awaiting: 0, inPicking: 0, packedToday: 0 });

  // Fetch packing queue from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [queueData, statsData] = await Promise.all([
        packingAPI.getQueue(),
        packingAPI.getStats()]
        );
        setPackingQueue(queueData || []);
        setStats({
          awaiting: statsData?.awaiting_packing || queueData?.length || 0,
          inPicking: statsData?.in_picking || 0,
          packedToday: statsData?.packed_today || 0
        });
      } catch (error) {
        console.error('Failed to fetch packing data:', error);
        toast.error('Failed to load packing queue');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle quick scan
  const handleQuickScan = async (result) => {
    if (result.valid) {
      // Check if it's an internal order/tote scan
      if (result.type === 'INTERNAL') {
        const matchingOrder = packingQueue.find((o) => (o.id || o.order_number) === result.raw) || packingQueue[0];
        if (matchingOrder) {
          setSelectedOrder(matchingOrder);
          setShowWorkflowDialog(true);
          toast.success('Tote/Order Found', {
            description: `Loading items for ${matchingOrder.id || matchingOrder.order_number}`
          });
        }
      } else {
        toast.info('Scan Recognized', {
          description: `${result.type}: ${result.raw}`
        });
      }
    }
  };

  // Start packing workflow
  const handleStartPacking = (order) => {
    setSelectedOrder(order);
    setShowWorkflowDialog(true);
  };

  // Handle workflow completion
  const handleWorkflowComplete = async (data) => {
    if (selectedOrder) {
      try {
        const orderId = selectedOrder.id || selectedOrder.order_number || selectedOrder.order_id;
        await packingAPI.completePacking(orderId);
        setOrderStatuses((prev) => ({
          ...prev,
          [orderId]: 'staged'
        }));
        // Refresh queue
        const queueData = await packingAPI.getQueue();
        setPackingQueue(queueData || []);
        toast.success('Packing Complete', {
          description: `Order ${orderId} packed and ready for dispatch. Label printed.`
        });
      } catch (error) {
        console.error('Failed to complete packing:', error);
        toast.error('Failed to complete packing');
      }
    }
  };

  // Handle label preview
  const handleLabelPreview = async (order) => {
    try {
      const orderId = order.id || order.order_number || order.order_id;
      // Generate a carton ID for demo purposes
      const cartonId = `CTN-${orderId}`;
      setSelectedCartonId(cartonId);

      // Fetch label preview data
      const previewData = await packingAPI.previewLabel(cartonId);
      setLabelData(previewData);
      setShowLabelPreview(true);
    } catch (error) {
      console.error('Failed to fetch label preview:', error);
      // Generate sample label data for demo
      const sampleZPL = `^XA
^PW812
^LL1218
^FO50,50^A0N,40,40^FDEzERP Warehouse^FS
^FO50,100^GB700,3,3^FS
^FO50,120^A0N,30,30^FDSHIP TO:^FS
^FO50,160^A0N,35,35^FD${order.customer || order.customer_name || 'Customer'}^FS
^FO50,200^A0N,28,28^FDOrder: ${order.id || order.order_number}^FS
^FO50,320^GB700,3,3^FS
^FO50,340^A0N,25,25^FDTRACKING:^FS
^FO50,380^BY3^BCN,100,Y,N,N^FD${order.id || '1234567890'}^FS
^FO50,510^A0N,25,25^FDITEMS: ${order.items?.length || order.item_count || 0}^FS
^XZ`;
      setLabelData({
        label_type: 'shipping',
        zpl_code: sampleZPL,
        content: {
          order_id: order.id || order.order_number,
          customer: order.customer || order.customer_name
        }
      });
      setSelectedCartonId(`CTN-${order.id || order.order_number}`);
      setShowLabelPreview(true);
    }
  };

  const handlePrintComplete = (printData) => {
    toast.success('Label Sent to Printer', {
      description: `${printData.copies} label(s) queued for printing`
    });
  };

  // Handle step completion
  const handleStepComplete = (stepIndex, scanResult) => {
    const step = packingWorkflowSteps[stepIndex];

    if (step.triggersAction === 'load_pack_list') {
      toast.info('Pack List Loaded', {
        description: 'Verify each item before packing'
      });
    } else if (step.triggersAction === 'verify_pack_item') {
      toast.success('Item Verified', {
        description: 'Item matches order - pack into carton'
      });
    } else if (step.triggersAction === 'seal_carton') {
      toast.success('Carton Sealed', {
        description: 'Generating shipping label...'
      });
    }
  };

  // Get effective status
  const getOrderStatus = (order) => {
    const orderId = order.id || order.order_number || order.order_id;
    return orderStatuses[orderId] || order.status;
  };

  return (
    <div className="space-y-6" data-testid="wms-packing">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("packing", "Packing")}</h1>
          <p className="text-muted-foreground mt-1">{t("scanverifiedPackingAnd", "Scan-verified packing and shipping label generation")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <Printer className="h-4 w-4" />{t("printLabels", "Print Labels")}

          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowWorkflowDialog(true)}>
            <ScanLine className="h-4 w-4" />{t("startPackWorkflow", "Start Pack Workflow")}

          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="card-elevated border-l-4 border-l-purple-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("awaitingPacking", "Awaiting Packing")}</p>
              <p className="text-2xl font-bold text-purple-600">{stats.awaiting}</p>
            </div>
            <Box className="h-8 w-8 text-purple-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("inPicking", "In Picking")}</p>
              <p className="text-2xl font-bold text-amber-600">{stats.inPicking}</p>
            </div>
            <Clock className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("packedToday", "Packed Today")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.packedToday}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-green-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Scan-Gated Packing Input */}
      <Card className="card-elevated bg-gradient-to-r from-purple-50/50 to-transparent dark:from-purple-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
              <ScanLine className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold">{t("scanverifiedPacking", "Scan-Verified Packing")}</h3>
              <p className="text-sm text-muted-foreground">{t("scanToteOr", "Scan tote or order barcode to verify contents before packing")}

              </p>
            </div>
            <Badge variant="outline" className="ml-auto gap-1">
              <CheckCircle2 className="h-3 w-3" />{t("qualityControl", "Quality Control")}

            </Badge>
          </div>
          <BarcodeScanner
            onScan={handleQuickScan}
            scanMode="internal"
            placeholder="Scan tote or order barcode to start packing..."
            showSamples />
          
        </CardContent>
      </Card>

      {/* Workflow Steps Guide */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <h4 className="font-medium mb-3">{t("scangatedPackingProcess", "Scan-Gated Packing Process")}</h4>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</div>
              <span>{t("scanTote", "Scan Tote")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold">2</div>
              <span>{t("verifyItems", "Verify Items")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold">3</div>
              <span>{t("sealCarton", "Seal Carton")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>{t("printLabel", "Print Label")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Ready for Packing */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">{t("ordersReadyFor", "Orders Ready for Packing")}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("orderId", "Order ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("customer", "Customer")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("dueDate", "Due Date")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("priority", "Priority")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ?
                <tr>
                    <td colSpan={7} className="py-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">{t("loadingOrders", "Loading orders...")}</p>
                    </td>
                  </tr> :
                packingQueue.length === 0 ?
                <tr>
                    <td colSpan={7} className="py-8 text-center">
                      <Box className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-sm text-muted-foreground">{t("noOrdersReady", "No orders ready for packing")}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t("completePickingTo", "Complete picking to see orders here")}</p>
                    </td>
                  </tr> :
                packingQueue.map((order) => {
                  const orderId = order.id || order.order_number || order.order_id;
                  const effectiveStatus = getOrderStatus(order);
                  return (
                    <tr key={orderId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{orderId}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-sm font-medium">{order.customer || order.customer_name || '-'}</p>
                          <p className="text-xs text-muted-foreground">{order.shippingMethod || order.shipping_method || '-'}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm">{order.dueDate || order.due_date || '-'}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-medium">{order.items?.length || order.item_count || 0}{t("items", "items")}</span>
                      </td>
                      <td className="py-4 px-4">
                        <PriorityBadge priority={order.priority || 'medium'} />
                      </td>
                      <td className="py-4 px-4">
                        <StatusIndicator status={effectiveStatus} />
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1"
                            onClick={() => handleLabelPreview(order)}
                            data-testid={`preview-label-${orderId}`}>
                            
                            <Eye className="h-3.5 w-3.5" />{t("previewLabel", "Preview Label")}

                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleStartPacking(order)}
                            data-testid={`pack-order-${orderId}`}>
                            
                            <ScanLine className="h-4 w-4 mr-1" />{t("pack", "Pack")}

                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Packing Station Tips */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("packingStation", "Packing Station")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="p-4 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                  <ScanLine className="h-4 w-4 text-blue-600" />
                </div>
                <h4 className="font-medium text-sm">{t("1ScanTote", "1. Scan Tote")}</h4>
              </div>
              <p className="text-xs text-muted-foreground">{t("scanTheOrder", "Scan the order tote to load items and start verification")}

              </p>
            </div>
            <div className="p-4 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
                  <CheckCircle2 className="h-4 w-4 text-purple-600" />
                </div>
                <h4 className="font-medium text-sm">{t("2VerifyContents", "2. Verify Contents")}</h4>
              </div>
              <p className="text-xs text-muted-foreground">{t("rescanEachItem", "Re-scan each item to verify order accuracy before packing")}

              </p>
            </div>
            <div className="p-4 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
                  <Printer className="h-4 w-4 text-green-600" />
                </div>
                <h4 className="font-medium text-sm">{t("3PrintShip", "3. Print & Ship")}</h4>
              </div>
              <p className="text-xs text-muted-foreground">{t("generateShippingLabel", "Generate shipping label and attach to sealed carton")}

              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scan Workflow Dialog */}
      <ScanWorkflowDialog
        open={showWorkflowDialog}
        onOpenChange={setShowWorkflowDialog}
        title={selectedOrder ? `Packing: ${selectedOrder.id}` : 'Packing Workflow'}
        icon={Box}
        steps={packingWorkflowSteps}
        expectedItems={samplePackingItems}
        onComplete={handleWorkflowComplete}
        onStepComplete={handleStepComplete}
        validationMode="flexible" />
      

      {/* Label Preview Modal */}
      <LabelPreviewModal
        open={showLabelPreview}
        onOpenChange={setShowLabelPreview}
        labelData={labelData}
        cartonId={selectedCartonId}
        onPrint={handlePrintComplete} />
      
    </div>);

};